import type {
  ModifiedPlayer,
  NFLPlayerInputs
} from '@library/types/simulations';
import { useEffect, useMemo, useState } from 'react';
import type { LeagueTeamPlayer } from '@library/types/player';
import { NumberInput } from '@library/components/inputs';
import type { OverrideValidationError } from '@bo-hooks/simulations/useSimulationDetailSteps';
import type { Updater } from 'use-immer';
import { classNames } from '@library/utils';
import { withinMinMax } from '@bo-utils/simulations';

export const NumberOverride = ({
  defaultPlayer = undefined,
  field,
  homeTeam,
  max = undefined,
  min = undefined,
  modifiedPlayer = undefined,
  onModifyPlayer,
  overrideValidationErrors,
  player,
  setOverrideValidationErrors
}: {
  defaultPlayer?: ModifiedPlayer;
  field: keyof NFLPlayerInputs;
  homeTeam: boolean;
  max?: number;
  min?: number;
  modifiedPlayer?: ModifiedPlayer;
  onModifyPlayer: ({
    homeTeam,
    playerId,
    playerName,
    value
  }: {
    homeTeam: boolean;
    playerId: string;
    playerName: string;
    value: Partial<NFLPlayerInputs>;
  }) => void;
  overrideValidationErrors: Array<OverrideValidationError>;
  player: LeagueTeamPlayer;
  setOverrideValidationErrors: Updater<Array<OverrideValidationError>>;
}) => {
  const handleModifyFloat = useMemo(
    () =>
      ({ value }: { value: number | '' }) => {
        onModifyPlayer({
          homeTeam,
          playerId: player.foreign_id,
          playerName: player.name,
          value: { [field]: value }
        });
      },
    [field, homeTeam, onModifyPlayer, player.foreign_id, player.name]
  );

  const { playerOverrides } = modifiedPlayer ?? {};
  const modifiedPlayerValue = playerOverrides?.[field];

  const { playerOverrides: defaultPlayerOverrides } = defaultPlayer ?? {};
  const defaultPlayerValue = defaultPlayerOverrides?.[field];

  const [value, setValue] = useState<number | ''>(
    modifiedPlayerValue !== undefined
      ? Number(modifiedPlayerValue)
      : Number(defaultPlayerValue)
  );

  const inputValidationFailed = useMemo(
    () => withinMinMax({ currentValue: value, minimum: min, maximum: max }),
    [max, min, value]
  );

  const overallValidationFailed = useMemo(() => {
    return overrideValidationErrors.find(
      (error) => error.field === field && error.playerId === undefined
    );
  }, [field, overrideValidationErrors]);

  useEffect(() => {
    const validationError = overrideValidationErrors.find(
      (error) => error.field === field && error.playerId === player.foreign_id
    );

    if (validationError && !inputValidationFailed) {
      setOverrideValidationErrors((draft) => {
        draft.splice(
          draft.findIndex(
            (error) =>
              error.field === field && error.playerId === player.foreign_id
          ),
          1
        );
      });
    } else if (!validationError && inputValidationFailed) {
      setOverrideValidationErrors((draft) => {
        draft.push({
          field,
          playerId: player.foreign_id
        });
      });
    }
  }, [
    field,
    overrideValidationErrors,
    player.foreign_id,
    setOverrideValidationErrors,
    inputValidationFailed
  ]);

  useEffect(() => {
    if (
      (modifiedPlayer &&
        modifiedPlayer.playerOverrides?.[field] !== undefined &&
        modifiedPlayer.playerOverrides[field] !== value) ||
      (!modifiedPlayer && value !== '')
    ) {
      handleModifyFloat({ value });
    }
  }, [field, handleModifyFloat, modifiedPlayer, value]);

  const [initialValue] = useState<number | ''>(value);

  const hasValueChanged = value !== initialValue;

  const errorBorder = inputValidationFailed || overallValidationFailed;

  return (
    <NumberInput
      className={classNames(
        'font-heebo text-soClean-100 disabled:text-soClean-160 h-8 w-16 rounded-md bg-transparent px-0 text-center text-sm font-medium leading-5 tracking-tight',
        errorBorder ? 'border-roses focus:border-roses' : '',
        !errorBorder && hasValueChanged
          ? 'border-msJackson focus:border-msJackson'
          : '',
        !errorBorder && !hasValueChanged
          ? 'border-soFresh-140 focus:border-msJackson'
          : ''
      )}
      onBlur={() => {
        if (value === '') {
          setValue(initialValue);
        }
      }}
      onValueChange={(inputValue) =>
        setValue(inputValue === '' ? inputValue : Number(inputValue))
      }
      value={value}
    />
  );
};
